/**
 * Appointments and Scheduling-related GraphQL queries
 * Appointment management, scheduling, and time slots
 */

export const APPOINTMENTS_QUERIES = {
  // Appointment Data queries
  GET_APPOINTMENT_DATES: `
    query GetAppointmentDates($facilityId: Int!) {
      pep_vw_available_clinic_appointments(
        where: {facility_id: {_eq: $facilityId}},
        distinct_on: appointment_date
      ) {
        appointment_date
        fmt_appointment_date
      }
    }
  `,

  GET_APPOINTMENT_TIMES: `
    query GetAppointmentTimes($facilityId: Int!, $appointmentDate: date!) {
      pep_vw_available_clinic_appointments(
        where: {
          facility_id: {_eq: $facilityId},
          _and: {appointment_date: {_eq: $appointmentDate}}
        }
      ) {
        appointment_hour
        fmt_appointment_hour
      }
    }
  `,

  // From GRAPHQL_QUERIES - Appointment Facilities
  GET_APPOINTMENT_FACILITIES: `
    query GetAppointmentFacilities($clinicId: Int!) {
      pep_vw_consolidated_data(where: {clinic_id: {_eq: $clinicId}}, distinct_on: facility_id) {
        facility_id
        facility_name
      }
    }
  `,

  // Get Appointment Dates (from GRAPHQL_QUERIES)
  GET_APPOINTMENT_DATES_SIMPLE: `
    query GetAppointmentDates($facilityId: Int!) {
      pep_vw_appointment_dates(where: {facility_id: {_eq: $facilityId}}) {
        appointment_date
        fmt_appointment_date
      }
    }
  `,

  // Get Appointment Times (from GRAPHQL_QUERIES)
  GET_APPOINTMENT_TIMES_SIMPLE: `
    query GetAppointmentTimes($facilityId: Int!, $appointmentDate: String!) {
      pep_vw_appointment_times(where: {facility_id: {_eq: $facilityId}, appointment_date: {_eq: $appointmentDate}}) {
        appointment_hour
        fmt_appointment_hour
      }
    }
  `,

  // All appointments without filter (from GRAPHQL_QUERIES)
  APPOINTMENT_SLOTS_APPOINTMENTS_ALL: `
    query MyQuery {
      pep_vw2_clinic_appointments(order_by: {appointment_date: desc}) {
        appointment_id
        clinic_name
        facility_name
        appointment_date
        appointment_start_time
        appointment_end_time
        patient_clinic_data_id
      }
    }
  `,

  // Filtered appointments by clinic (from GRAPHQL_QUERIES)
  APPOINTMENT_SLOTS_APPOINTMENTS_FILTERED: `
    query MyQuery($clinicID: Int!) {
      pep_vw2_clinic_appointments(where: {clinic_id: {_eq: $clinicID}}, order_by: {appointment_date: desc}) {
        appointment_id
        clinic_name
        facility_name
        appointment_date
        appointment_start_time
        appointment_end_time
        patient_clinic_data_id
        clinic_id
        facility_id
      }
    }
  `,

  // Advanced filtered appointments with multiple filter parameters
  APPOINTMENT_SLOTS_APPOINTMENTS_ADVANCED_FILTER: `
    query MyQuery(
      $clinicID: Int,
      $facilityIDs: [Int!],
      $startDate: date,
      $endDate: date,
      $startTime: String,
      $endTime: String
    ) {
      pep_vw2_clinic_appointments(
        where: {
          _and: [
            {clinic_id: {_eq: $clinicID}},
            {facility_id: {_in: $facilityIDs}},
            {appointment_date: {_gte: $startDate}},
            {appointment_date: {_lte: $endDate}},
            {appointment_start_time: {_gte: $startTime}},
            {appointment_end_time: {_lte: $endTime}}
          ]
        },
        order_by: {appointment_date: desc}
      ) {
        appointment_id
        clinic_name
        facility_name
        appointment_date
        appointment_start_time
        appointment_end_time
        patient_clinic_data_id
        clinic_id
        facility_id
      }
    }
  `,

  // Flexible filtered appointments that supports optional parameters
  APPOINTMENT_SLOTS_APPOINTMENTS_FLEXIBLE_FILTER: `
    query MyQuery(
      $clinicFilter: pep_vw2_clinic_appointments_bool_exp = {},
      $facilityFilter: pep_vw2_clinic_appointments_bool_exp = {},
      $dateFilter: pep_vw2_clinic_appointments_bool_exp = {},
      $timeFilter: pep_vw2_clinic_appointments_bool_exp = {}
    ) {
      pep_vw2_clinic_appointments(
        where: {
          _and: [
            $clinicFilter,
            $facilityFilter,
            $dateFilter,
            $timeFilter
          ]
        },
        order_by: {appointment_date: desc}
      ) {
        appointment_id
        clinic_name
        facility_name
        appointment_date
        appointment_start_time
        appointment_end_time
        patient_clinic_data_id
        clinic_id
        facility_id
      }
    }
  `,

  // Get activity appointment details for editing
  GET_ACTIVITY_APPOINTMENT_DETAILS: `
    query GetActivityAppointmentDetails($activityId: Int!) {
      pep_vw_appointment_details(where: {activity_id: {_eq: $activityId}}) {
        activity_id
        appointment_date
        appointment_end_time
        appointment_id
        appointment_start_time
        appointment_user_patient_activity_mapper_id
        user_id
      }
    }
  `,
  // Get total count of appointments with optional filters
  APPOINTMENT_SLOTS_COUNT_AGGREGATE: `
    query AppointmentSlotsCountAggregate(
      $clinicFilter: pep_vw2_clinic_appointments_bool_exp = {},
      $facilityFilter: pep_vw2_clinic_appointments_bool_exp = {},
      $dateFilter: pep_vw2_clinic_appointments_bool_exp = {},
      $timeFilter: pep_vw2_clinic_appointments_bool_exp = {}
    ) {
      pep_vw2_clinic_appointments_aggregate(
        where: {
          _and: [
            $clinicFilter,
            $facilityFilter,
            $dateFilter,
            $timeFilter
          ]
        }
      ) {
        aggregate {
          count
        }
      }
    }
  `,

  // Paginated appointments query with limit and offset
  APPOINTMENT_SLOTS_APPOINTMENTS_PAGINATED: `
    query AppointmentSlotsPaginated(
      $limit: Int!,
      $offset: Int!,
      $clinicFilter: pep_vw2_clinic_appointments_bool_exp = {},
      $facilityFilter: pep_vw2_clinic_appointments_bool_exp = {},
      $dateFilter: pep_vw2_clinic_appointments_bool_exp = {},
      $timeFilter: pep_vw2_clinic_appointments_bool_exp = {}
    ) {
      pep_vw2_clinic_appointments(
        limit: $limit,
        offset: $offset,
        where: {
          _and: [
            $clinicFilter,
            $facilityFilter,
            $dateFilter,
            $timeFilter
          ]
        },
        order_by: {appointment_date: desc}
      ) {
        appointment_id
        clinic_name
        facility_name
        appointment_date
        appointment_start_time
        appointment_end_time
        patient_clinic_data_id
        clinic_id
        facility_id
      }
    }
  `,

  // Get appointment details for enrollment
  GET_APPOINTMENT_DETAILS: `
    query GetAppointmentDetails($where: pep_vw_appointment_details_bool_exp) {
      pep_vw_appointment_details(where: $where, order_by: {appointment_date: asc, appointment_start_time: asc}) {
        activity_id
        appointment_date
        appointment_end_time
        appointment_id
        appointment_start_time
        appointment_user_patient_activity_mapper_id
        user_id
      }
    }
  `
};